import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import {
  ELECTRICAL_FUNCTIONS,
  parseElectricalValue,
} from '../../utils/electrical-calibration-profile';
import {
  DEFAULT_VALIDITY_MONTHS,
  addValidityDaysToIsoDate,
  computeElectricalPointMetrics,
  createElectricalSession,
  getElectricalSession,
  updateElectricalSession,
  listElectricalSessionsByInstrument,
  listElectricalBlocks,
  setBlockApplicable,
  updateBlock,
  saveElectricalBlockPoints,
  listElectricalPoints,
  calculateElectricalResults,
  listElectricalResults,
  closeElectricalSession,
  getTodayDailyEnvironmentLog,
  upsertTodayDailyEnvironmentLog,
} from '../../utils/api-electrical-calibration';

const STD_ACCURACY = 0.05; // clase del patrón GFUVE
const EDITABLE_COLS = ['pattern', 'instrument'];
const VALIDITY_OPTIONS = [
  { value: 6, label: '6 meses' },
  { value: 12, label: '1 año' },
  { value: 24, label: '2 años' },
];

function buildEmptyRows(n = 6) {
  return Array.from({ length: n }, (_, i) => ({ point_no: i + 1, pattern: '', instrument: '' }));
}
function toIsoDate(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}
function formatEsDate(isoDate) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return '—';
  const d = new Date(`${base}T12:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR');
}
function toNullableNumber(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
// Valor ambiental "válido": null/0 se consideran "sin dato" (para poder caer al
// log del día). En un laboratorio real nunca es 0 °C / 0 %HR / 0 hPa.
function envValue(v) {
  const n = toNullableNumber(v);
  return n && n !== 0 ? n : null;
}
function fmt(v, d = 4) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : '-';
}

// Reconstruye rows por bloque a partir de los puntos guardados
function pointsToRowsByBlock(blocks, points) {
  const byBlock = {};
  for (const p of points) {
    (byBlock[p.block_id] = byBlock[p.block_id] || {})[p.point_no] = p;
  }
  const out = {};
  for (const b of blocks) {
    const n = b.point_count || 6;
    out[b.block_key] = Array.from({ length: n }, (_, i) => {
      const p = byBlock[b.id]?.[i + 1];
      return {
        point_no: i + 1,
        pattern: p ? (p.pattern_reading_raw ?? (p.pattern_reading ?? '')) : '',
        instrument: p ? (p.instrument_reading_raw ?? (p.instrument_reading ?? '')) : '',
      };
    });
  }
  return out;
}

export default function ElectricalCalibrationPage() {
  const { instrumentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSessionId = searchParams.get('sessionId') || '';

  const inputRefs = useRef({});

  const [instrument, setInstrument] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [session, setSession] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [rowsByBlock, setRowsByBlock] = useState({});
  const [results, setResults] = useState([]);
  const [activeKey, setActiveKey] = useState(ELECTRICAL_FUNCTIONS[0].key);

  const [loading, setLoading] = useState(true);
  const [runningWorkflow, setRunningWorkflow] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [closing, setClosing] = useState(false);
  const [savingDailyEnv, setSavingDailyEnv] = useState(false);
  const [uiNotice, setUiNotice] = useState(null);
  const [dailyEnv, setDailyEnv] = useState({ pressure_hpa: 0, temp_c: 0, humidity_rh: 0, pressure_unc_hpa: 1, temp_unc_c: 1, humidity_unc_rh: 3, notes: '' });

  const showNotice = (message, type = 'info') => setUiNotice({ message, type });
  const closeNotice = () => setUiNotice(null);
  const normalizeDailyEnv = (s = null) => ({
    pressure_hpa: Number(s?.pressure_hpa ?? 0), temp_c: Number(s?.temp_c ?? 0), humidity_rh: Number(s?.humidity_rh ?? 0),
    pressure_unc_hpa: Number(s?.pressure_unc_hpa ?? 1), temp_unc_c: Number(s?.temp_unc_c ?? 1), humidity_unc_rh: Number(s?.humidity_unc_rh ?? 3), notes: s?.notes || '',
  });

  const activeBlock = useMemo(() => blocks.find((b) => b.block_key === activeKey) || null, [blocks, activeKey]);
  const activeFn = useMemo(() => ELECTRICAL_FUNCTIONS.find((f) => f.key === activeKey), [activeKey]);
  const activeRows = rowsByBlock[activeKey] || buildEmptyRows(activeBlock?.point_count || 6);
  const validityMonths = Number(session?.validity_months || DEFAULT_VALIDITY_MONTHS);
  const calibrationBaseDate = session?.calibration_date || session?.report_issue_date || toIsoDate();
  const validUntilPreview = addValidityDaysToIsoDate(calibrationBaseDate, validityMonths);
  const classPct = toNullableNumber(session?.instrument_class);
  const accuracyClass = Number(session?.standard_accuracy_class ?? STD_ACCURACY);
  const driftClass = Number(session?.standard_drift_class ?? 0);
  // Resolución del bloque (función) activo; cae a la de sesión / instrumento.
  const activeResolution = Number(
    activeBlock?.resolution ?? session?.instrument_resolution ?? instrument?.resolution ?? 0
  );

  // Resultados en vivo del bloque activo
  const liveResults = useMemo(() => {
    return (activeRows || [])
      .map((r) => {
        const pat = parseElectricalValue(r.pattern);
        const ins = parseElectricalValue(r.instrument);
        const m = computeElectricalPointMetrics({
          patternValue: pat.value,
          instrumentValue: ins.value,
          resolution: activeResolution,
          accuracyClass,
          driftClass,
          classPct,
        });
        return m ? { point_no: r.point_no, ...m } : null;
      })
      .filter(Boolean);
  }, [activeRows, activeResolution, accuracyClass, driftClass, classPct]);

  const setBlockResolutionLocal = (blockId, value) => {
    setBlocks((prev) => prev.map((b) => (b.id === blockId ? { ...b, resolution: value } : b)));
  };

  // ── carga ──
  async function loadInstrument() {
    const { data, error } = await supabase
      .from('instruments')
      .select('id, tag, lab_id, brand, model, serial, type, unit, range_min, range_max, resolution, tolerance, client:client_id (name)')
      .eq('id', instrumentId).single();
    if (error) throw error;
    setInstrument(data);
  }
  async function loadSessions() {
    const list = await listElectricalSessionsByInstrument(instrumentId);
    setSessions(list);
    if (!sessionId && !initialSessionId && list.length) setSessionId(list[0].id);
  }
  async function loadDailyEnvironment() {
    try { setDailyEnv(normalizeDailyEnv(await getTodayDailyEnvironmentLog())); } catch {}
  }
  async function loadSessionData(id) {
    if (!id) { setSession(null); setBlocks([]); setRowsByBlock({}); setResults([]); return; }
    const [s, blks, pts, res] = await Promise.all([
      getElectricalSession(id), listElectricalBlocks(id), listElectricalPoints(id), listElectricalResults(id),
    ]);
    setSession(s);
    setBlocks(blks);
    setRowsByBlock(pointsToRowsByBlock(blks, pts));
    setResults(res || []);
  }

  useEffect(() => {
    (async () => {
      try { setLoading(true); await loadInstrument(); await loadSessions(); await loadDailyEnvironment(); }
      catch (e) { console.error('[Electrical] load', e); showNotice('No se pudo cargar el módulo eléctrico.', 'error'); }
      finally { setLoading(false); }
    })();
  }, [instrumentId]);
  useEffect(() => {
    loadSessionData(sessionId).catch((e) => { console.error('[Electrical] session', e); showNotice('No se pudo cargar la sesión.', 'error'); });
  }, [sessionId]);

  // ── edición de la grilla ──
  const updateCell = (rowIndex, colKey, value) => {
    setRowsByBlock((prev) => {
      const rows = (prev[activeKey] || buildEmptyRows(activeBlock?.point_count || 6)).map((r, i) => (i === rowIndex ? { ...r, [colKey]: value } : r));
      return { ...prev, [activeKey]: rows };
    });
  };
  const focusNextCell = (rowIndex, colKey) => {
    const colIndex = EDITABLE_COLS.indexOf(colKey);
    if (colIndex < 0) return;
    let nextRow = rowIndex + 1, nextCol = colIndex;
    if (nextRow >= activeRows.length) { nextRow = 0; nextCol = colIndex + 1; }
    if (nextCol >= EDITABLE_COLS.length) return;
    const ref = inputRefs.current[`${activeKey}-${nextRow}-${EDITABLE_COLS[nextCol]}`];
    if (ref?.focus) { ref.focus(); ref.select?.(); }
  };

  // ── handlers de sesión ──
  const handleCreateSession = async () => {
    try {
      const created = await createElectricalSession({
        instrumentId,
        serviceOrderCode: searchParams.get('serviceOrderCode') || null,
        certificateType: 'NO_SAC',
      });
      await loadSessions();
      setSessionId(created.id);
      showNotice('Sesión eléctrica creada con las 8 funciones.', 'success');
    } catch (e) { console.error('[Electrical] create', e); showNotice('No se pudo crear la sesión.', 'error'); }
  };

  const persistParamsAndPoints = async () => {
    if (!sessionId || !session) return;
    const months = Number(session.validity_months || DEFAULT_VALIDITY_MONTHS);
    await updateElectricalSession(sessionId, {
      instrument_resolution: toNullableNumber(session.instrument_resolution),
      instrument_class: toNullableNumber(session.instrument_class),
      // Si la sesión no tiene ambiente cargado, cae automáticamente al log del día
      // (evita el paso manual "Aplicar a la sesión", que se olvidaba y dejaba el PDF en 0).
      ambient_pressure_avg: envValue(session.ambient_pressure_avg) ?? envValue(dailyEnv.pressure_hpa),
      ambient_temp_avg: envValue(session.ambient_temp_avg) ?? envValue(dailyEnv.temp_c),
      ambient_humidity_avg: envValue(session.ambient_humidity_avg) ?? envValue(dailyEnv.humidity_rh),
      validity_months: months,
      valid_until: addValidityDaysToIsoDate(session.calibration_date || session.report_issue_date || toIsoDate(), months),
      notes: session.notes || '',
      status: 'in_progress',
    });
    // guardar resolución por función + puntos de cada bloque
    for (const b of blocks) {
      await updateBlock(b.id, { resolution: toNullableNumber(b.resolution) });
      const rows = rowsByBlock[b.block_key];
      if (rows) await saveElectricalBlockPoints(sessionId, b, rows);
    }
  };

  const handleSaveAllAndCalculate = async () => {
    if (!sessionId || !session) { showNotice('Primero creá o seleccioná una sesión.', 'info'); return; }
    try {
      setRunningWorkflow(true);
      await persistParamsAndPoints();
      await calculateElectricalResults(sessionId);
      await loadSessionData(sessionId);
      showNotice('Guardado y cálculo completos.', 'success');
    } catch (e) { console.error('[Electrical] save-all', e); showNotice('No se pudo completar el guardado/cálculo.', 'error'); }
    finally { setRunningWorkflow(false); }
  };

  const handleCalculate = async () => {
    if (!sessionId) return;
    try { setCalculating(true); await persistParamsAndPoints(); await calculateElectricalResults(sessionId); await loadSessionData(sessionId); showNotice('Cálculo completado.', 'success'); }
    catch (e) { console.error('[Electrical] calc', e); showNotice('No se pudo calcular.', 'error'); }
    finally { setCalculating(false); }
  };

  const handleToggleApplicable = async (block) => {
    try {
      const updated = await setBlockApplicable(block.id, !block.applicable);
      setBlocks((prev) => prev.map((b) => (b.id === block.id ? { ...b, applicable: updated.applicable } : b)));
    } catch (e) { console.error('[Electrical] toggle', e); showNotice('No se pudo cambiar "no aplica".', 'error'); }
  };

  const handleClose = async () => {
    if (!sessionId) return;
    if (!confirm('¿Cerrar la sesión eléctrica?')) return;
    try { setClosing(true); await closeElectricalSession(sessionId); await loadSessionData(sessionId); await loadSessions(); showNotice('Sesión cerrada.', 'success'); }
    catch (e) { console.error('[Electrical] close', e); showNotice('No se pudo cerrar la sesión.', 'error'); }
    finally { setClosing(false); }
  };

  const handleSaveDailyEnvironment = async () => {
    try { setSavingDailyEnv(true); setDailyEnv(normalizeDailyEnv(await upsertTodayDailyEnvironmentLog(dailyEnv))); showNotice('Condiciones del día guardadas.', 'success'); }
    catch (e) { console.error(e); showNotice('No se pudieron guardar las condiciones.', 'error'); }
    finally { setSavingDailyEnv(false); }
  };
  const handleApplyDailyToSession = () => {
    if (!session) { showNotice('Primero creá o seleccioná una sesión.', 'info'); return; }
    setSession((s) => ({ ...s, ambient_pressure_avg: dailyEnv.pressure_hpa, ambient_temp_avg: dailyEnv.temp_c, ambient_humidity_avg: dailyEnv.humidity_rh }));
    showNotice('Condiciones aplicadas a la sesión (recordá guardar).', 'success');
  };

  if (loading) return <div className="p-6">Cargando calibración eléctrica…</div>;

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Calibración Eléctrica (Multímetro / Amperímetro / Ohmímetro)</h1>
          <p className="text-sm text-slate-200">
            Instrumento: <strong>{instrument?.tag || instrument?.lab_id || instrument?.id}</strong> · {instrument?.brand} {instrument?.model} · Cliente: {instrument?.client?.name || '-'}
          </p>
          <p className="text-xs text-slate-300 mt-1">Patrón: <strong>{session?.standard_name || 'GFUVE GF6018A'}</strong> (S/N {session?.standard_serial || '20250406'})</p>
        </div>
        <button className="px-3 py-2 border rounded" onClick={() => navigate(`/instrument-details/${instrumentId}`)}>Volver a instrumento</button>
      </div>

      <div className="border rounded-lg p-4 space-y-4 bg-card">
        {/* Condiciones ambientales del día */}
        <div className="border border-slate-200 rounded-lg p-3 bg-white text-slate-900">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div>
              <div className="text-sm font-semibold">Condiciones ambientales del día (laboratorio)</div>
              <div className="text-xs text-slate-700">Se cargan una vez por la mañana y valen para todas las calibraciones del día.</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button className="px-3 py-2 border rounded" onClick={handleSaveDailyEnvironment} disabled={savingDailyEnv}>{savingDailyEnv ? 'Guardando…' : 'Guardar condiciones del día'}</button>
              <button className="px-3 py-2 border rounded" onClick={handleApplyDailyToSession}>Aplicar a sesión actual</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
            <NumberField tone="light" label="Presión atmosférica (hPa)" value={dailyEnv.pressure_hpa} onChange={(v) => setDailyEnv((d) => ({ ...d, pressure_hpa: v }))} />
            <NumberField tone="light" label="Temperatura (°C)" value={dailyEnv.temp_c} onChange={(v) => setDailyEnv((d) => ({ ...d, temp_c: v }))} />
            <NumberField tone="light" label="Humedad (%HR)" value={dailyEnv.humidity_rh} onChange={(v) => setDailyEnv((d) => ({ ...d, humidity_rh: v }))} />
          </div>
        </div>

        {/* Selector de sesión */}
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="block text-xs text-slate-200 mb-1">Sesión eléctrica</label>
            <select className="border rounded px-3 py-2 min-w-[420px] bg-white text-slate-900" value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
              <option value="">Seleccionar…</option>
              {sessions.map((s) => (<option key={s.id} value={s.id}>{new Date(s.created_at).toLocaleString()} · {s.status} · {s.certificate_type}</option>))}
            </select>
          </div>
          <button className="px-3 py-2 bg-emerald-600 text-white rounded" onClick={handleCreateSession}>Nueva sesión eléctrica</button>
        </div>

        {/* Parámetros de sesión */}
        {session && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
              <Field label="Estado" value={session.status} />
              <Field label="Tipo cert." value={session.certificate_type} />
              <NumberField label="Resolución (por defecto)" value={session.instrument_resolution ?? instrument?.resolution ?? ''} onChange={(v) => setSession((s) => ({ ...s, instrument_resolution: v }))} />
              <NumberField label="Clase / tolerancia (%)" value={session.instrument_class ?? ''} onChange={(v) => setSession((s) => ({ ...s, instrument_class: v }))} />
              <div className="lg:col-span-2">
                <label className="block text-xs text-slate-200 mb-1">Validez del certificado</label>
                <select className="w-full border rounded px-2 py-1 bg-white text-slate-900 border-slate-300" value={String(validityMonths)} onChange={(e) => setSession((s) => ({ ...s, validity_months: Number(e.target.value) }))}>
                  {VALIDITY_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                </select>
                <div className="text-xs text-slate-300 mt-1">Vence el: <span className="font-semibold text-slate-100">{formatEsDate(validUntilPreview)}</span></div>
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-200 mb-1">Observaciones del técnico (aparecen en el certificado)</label>
              <textarea className="w-full min-h-[70px] border rounded px-3 py-2" value={session.notes || ''} onChange={(e) => setSession((s) => ({ ...s, notes: e.target.value }))} placeholder="Ej: se observa fusible de escala mA dañado." />
            </div>
            <div className="flex gap-3 flex-wrap">
              <button className="px-3 py-2 bg-blue-700 text-white rounded" onClick={handleSaveAllAndCalculate} disabled={runningWorkflow}>{runningWorkflow ? 'Guardando y calculando…' : 'Guardar todo y calcular'}</button>
              <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={handleCalculate} disabled={calculating || runningWorkflow}>{calculating ? 'Calculando…' : 'Calcular resultados'}</button>
              <button className="px-3 py-2 bg-violet-600 text-white rounded" onClick={handleClose} disabled={closing}>{closing ? 'Cerrando…' : 'Cerrar sesión'}</button>
            </div>
            {/* El certificado lo emite el JEFE al aprobar (queda registrado y subido
                a la carpeta del cliente). Si el técnico lo generara acá, saldrían
                PDFs sin registrar. */}
            {!!results.length && (
              <div className="rounded border border-emerald-600/40 bg-emerald-600/10 px-3 py-2 text-sm text-emerald-900">
                ✓ Resultados calculados. El <b>jefe de laboratorio</b> los revisa y emite el certificado desde
                “Certificados pendientes de aprobación”.
              </div>
            )}
          </>
        )}
      </div>

      {/* Pestañas por función + grilla */}
      {session && (
        <div className="border rounded-lg p-4 bg-card">
          <div className="flex flex-wrap gap-1 mb-4 border-b border-white/10">
            {ELECTRICAL_FUNCTIONS.map((f) => {
              const b = blocks.find((x) => x.block_key === f.key);
              const na = b && !b.applicable;
              const isActive = f.key === activeKey;
              return (
                <button key={f.key} onClick={() => setActiveKey(f.key)}
                  className={`px-3 py-2 text-sm rounded-t-md border-b-2 transition ${isActive ? 'border-sky-400 text-sky-200 bg-white/5' : 'border-transparent text-slate-300 hover:text-white'} ${na ? 'opacity-45 line-through' : ''}`}
                  title={na ? 'Marcada como No aplica' : ''}>
                  {f.label}
                </button>
              );
            })}
          </div>

          {activeBlock && (
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h2 className="text-lg font-semibold">{activeFn?.label} <span className="text-sm font-normal text-slate-400">{activeFn?.unitLabel}</span></h2>
              <div className="flex items-center gap-5 flex-wrap">
                {activeBlock.applicable && (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <span className="text-slate-200">Resolución en {activeFn?.label}:</span>
                    <input type="number" step="any" value={activeBlock.resolution ?? ''} onChange={(e) => setBlockResolutionLocal(activeBlock.id, e.target.value)} className="w-24 border rounded px-2 py-1 bg-white text-slate-900" placeholder={String(session?.instrument_resolution ?? '—')} />
                    <span className="text-slate-400">{activeFn?.unit}</span>
                  </label>
                )}
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input type="checkbox" checked={!activeBlock.applicable} onChange={() => handleToggleApplicable(activeBlock)} className="h-4 w-4 accent-amber-500" />
                  <span className="text-amber-300">Esta función NO se calibró (N/A)</span>
                </label>
              </div>
            </div>
          )}

          {activeBlock && !activeBlock.applicable ? (
            <div className="rounded border border-amber-400/40 bg-amber-500/10 text-amber-200 px-4 py-6 text-center text-sm">
              Función marcada como <strong>No aplica</strong> — no se incluirá en el cálculo ni en el certificado.
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-300 mb-2">Enter: baja a la siguiente fila y, al final, pasa a la siguiente columna (estilo Excel). Resistencia acepta multiplicadores: <code>1K</code>, <code>50K</code>, <code>5M</code>.</p>
              <div className="overflow-auto">
                <table className="min-w-[560px] text-sm border-collapse w-full">
                  <thead>
                    <tr className="bg-muted/50 text-center">
                      <th className="border px-2 py-1">Punto</th>
                      <th className="border px-2 py-1">Patrón {activeFn?.unitLabel}</th>
                      <th className="border px-2 py-1">Instrumento {activeFn?.unitLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRows.map((r, idx) => (
                      <tr key={r.point_no}>
                        <td className="border px-2 py-1 text-center text-slate-300">{r.point_no}</td>
                        <TextCell colKey="pattern" rowIndex={idx} value={r.pattern} onChange={(v) => updateCell(idx, 'pattern', v)} onEnter={focusNextCell} inputRef={(el) => { inputRefs.current[`${activeKey}-${idx}-pattern`] = el; }} />
                        <TextCell colKey="instrument" rowIndex={idx} value={r.instrument} onChange={(v) => updateCell(idx, 'instrument', v)} onEnter={focusNextCell} inputRef={(el) => { inputRefs.current[`${activeKey}-${idx}-instrument`] = el; }} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Resultados en vivo del bloque activo */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold mb-2">Resultados en vivo — {activeFn?.label}</h3>
                {!liveResults.length ? (
                  <p className="text-sm text-slate-400">Cargá patrón e instrumento para ver el cálculo.</p>
                ) : (
                  <div className="overflow-auto">
                    <table className="min-w-[960px] text-sm border-collapse w-full">
                      <thead>
                        <tr className="bg-muted/50">
                          <th className="border px-2 py-1">Pto</th>
                          <th className="border px-2 py-1">Patrón</th>
                          <th className="border px-2 py-1">Instrumento</th>
                          <th className="border px-2 py-1">Error</th>
                          <th className="border px-2 py-1">Error %</th>
                          <th className="border px-2 py-1" title="Incertidumbre del calibrador">u calibr.</th>
                          <th className="border px-2 py-1" title="Deriva del calibrador">u deriva</th>
                          <th className="border px-2 py-1" title="Resolución del instrumento">u resol.</th>
                          <th className="border px-2 py-1" title="Incertidumbre combinada">uc</th>
                          <th className="border px-2 py-1">U (k=2)</th>
                          <th className="border px-2 py-1">U reportada</th>
                          <th className="border px-2 py-1">En tol.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {liveResults.map((r) => (
                          <tr key={r.point_no} className={r.in_tolerance === false ? 'bg-red-50/70' : ''}>
                            <td className="border px-2 py-1 text-center">{r.point_no}</td>
                            <td className="border px-2 py-1">{fmt(r.pattern_mean)}</td>
                            <td className="border px-2 py-1">{fmt(r.instrument_mean)}</td>
                            <td className="border px-2 py-1">{fmt(r.error_abs)}</td>
                            <td className="border px-2 py-1">{r.error_pct == null ? '-' : fmt(r.error_pct) + ' %'}</td>
                            <td className="border px-2 py-1 text-slate-500">{fmt(r.u_standard)}</td>
                            <td className="border px-2 py-1 text-slate-500">{fmt(r.u_drift)}</td>
                            <td className="border px-2 py-1 text-slate-500">{fmt(r.u_resolution)}</td>
                            <td className="border px-2 py-1">{fmt(r.uc)}</td>
                            <td className="border px-2 py-1">{fmt(r.u_expanded)}</td>
                            <td className="border px-2 py-1 font-semibold">{fmt(r.u_reported)}</td>
                            <td className="border px-2 py-1 text-center">{r.in_tolerance == null ? '—' : (r.in_tolerance ? '✓' : '✗')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-slate-400 mt-1">U = k·uc (k=2, ~95%) · uc = √(u_calibr² + u_deriva² + u_resol²) · U reportada = máx(U, resolución).</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <StatusModal open={Boolean(uiNotice)} type={uiNotice?.type} message={uiNotice?.message} onClose={closeNotice} />
    </div>
  );
}

function TextCell({ value, onChange, inputRef, rowIndex, colKey, onEnter }) {
  return (
    <td className="border px-1 py-1">
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEnter?.(rowIndex, colKey); } }}
        className="w-full border rounded px-2 py-1"
      />
    </td>
  );
}
function Field({ label, value }) {
  return (<div><div className="text-xs text-slate-200">{label}</div><div className="border rounded px-2 py-1 bg-background">{value ?? '-'}</div></div>);
}
function NumberField({ label, value, onChange, tone = 'dark' }) {
  const labelClass = tone === 'light' ? 'text-slate-700' : 'text-slate-200';
  return (
    <div>
      <div className={`text-xs ${labelClass}`}>{label}</div>
      <input type="number" step="any" value={value ?? 0} onChange={(e) => onChange?.(e.target.value)} className="w-full border rounded px-2 py-1 bg-white text-slate-900" />
    </div>
  );
}
function StatusModal({ open, type = 'info', message = '', onClose }) {
  if (!open) return null;
  const themes = {
    success: { border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-900', badge: 'bg-emerald-600', label: 'Éxito' },
    error: { border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-900', badge: 'bg-red-600', label: 'Error' },
    info: { border: 'border-slate-200', bg: 'bg-slate-50', text: 'text-slate-900', badge: 'bg-slate-700', label: 'Info' },
  };
  const t = themes[type] || themes.info;
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/30 p-4">
      <div className={`w-full max-w-md rounded-xl border shadow-xl ${t.border} ${t.bg} ${t.text}`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10">
          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold text-white ${t.badge}`}>{t.label}</span>
          <button className="text-sm px-2 py-1 rounded border border-black/20 hover:bg-white/70" onClick={onClose}>Cerrar</button>
        </div>
        <div className="px-4 py-4 text-sm">{message}</div>
        <div className="px-4 pb-4 flex justify-end"><button className="px-3 py-2 rounded bg-slate-900 text-white text-sm hover:bg-slate-800" onClick={onClose}>Aceptar</button></div>
      </div>
    </div>
  );
}
